import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect
} from 'react';
import { FormSpy } from 'react-final-form';
import { CopyContactsContext } from './CopyContactsContext';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import {
  Loading,
  ReferenceArrayInput,
  useQuery,
  useNotify,
  useRefresh,
  useDataProvider,
  RecordContextProvider
} from 'react-admin';
import GridInput from '../../../core/components/inputs/GridInput';
import EntitlementApi from '../../../core/apis/EntitlementApi';
import withStyles from '@material-ui/core/styles/withStyles';
import GridPopover from '../../../core/components/grid/GridPopover';
import { uniq, intersectionBy, orderBy } from 'lodash';
import { getIcError } from '../../../core/providers/utils';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';

const styles = {
  button: {
    padding: '0'
  },
  loading: {
    width: '100%',
    height: '100%',
    display: 'content',
    marginTop: '0px'
  },
  paper: {
    '& > div': {
      maxHeight: 'none'
    }
  },
  tableWrapper: {
    maxHeight: 'calc(100vh - 250px)',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  }
};
const GridInputTable = ({ groups, index }) => {
  return (
    <ReferenceArrayInput
      label=" "
      optionTextLabel="Permission Group Name"
      optionValueLabel="group ID"
      resource="groups"
      reference="groups"
      source={`users[${index}].selectedGroups`}
      perPage={-1}
      sort={{ field: 'name', order: 'ASC' }}
      filter={{
        id: `[${groups}]`
      }}
    >
      <GridInput />
    </ReferenceArrayInput>
  );
};
const ContactsList = ({ classes, userTree, selectedUsers }) => {
  const [selectedRows, setSelectedRows] = useState(userTree);
  const { contactsContext, setContactsContext } = useContext(
    CopyContactsContext
  );
  selectedUsers.current = selectedRows;
  /**
   * Calculating number of rows depending of the filter
   */
  const numSelectedRows = useMemo(() => {
    const newArray = [];
    userTree.forEach(item => {
      if (selectedRows.includes(item)) {
        newArray.push(item);
      }
    });
    return newArray.length;
  }, [userTree, selectedRows]);

  const rowCount = userTree.length;

  const handleClickAll = useCallback(() => {
    if (numSelectedRows === rowCount) {
      setContactsContext({
        ...contactsContext,
        usersSelected: []
      });
      setSelectedRows([]);
    } else if (numSelectedRows !== rowCount) {
      setContactsContext({
        ...contactsContext,
        usersSelected: userTree
      });
      setSelectedRows(userTree);
    }
  }, [
    numSelectedRows,
    rowCount,
    setContactsContext,
    contactsContext,
    userTree
  ]);

  /**
   * When we click on the checkbox in a row
   * @param {*} checked
   * @param {*} item
   */
  const handleRowSelect = useCallback(
    (checked, item) => {
      if (checked) {
        setContactsContext({
          ...contactsContext,
          usersSelected: selectedRows.concat(item)
        });
        setSelectedRows(selectedRows => selectedRows.concat(item));
      } else {
        setContactsContext({
          ...contactsContext,
          usersSelected: selectedRows.filter(itemRow => item !== itemRow)
        });
        setSelectedRows(selectedRows =>
          selectedRows.filter(itemRow => item !== itemRow)
        );
      }
    },
    [contactsContext, selectedRows, setContactsContext]
  );

  return (
    <>
      <div className={classes.tableWrapper}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    numSelectedRows > 0 && numSelectedRows < rowCount
                  }
                  checked={numSelectedRows === rowCount && rowCount > 0}
                  onClick={handleClickAll}
                />
              </TableCell>
              <TableCell>Contact Name</TableCell>
              <TableCell>Permission Groups</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userTree &&
              userTree.map((user, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        onClick={event =>
                          handleRowSelect(event.target.checked, user)
                        }
                        checked={selectedRows.includes(user)}
                      />
                    </TableCell>
                    <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                    <TableCell>
                      <GridPopover
                        text="Manage"
                        classes={{
                          paper: classes.paper,
                          button: classes.button
                        }}
                        popoverContent={
                          <GridInputTable groups={user.groups} index={index} />
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
      <Grid item xs={12}>
        <Grid container justifyContent="flex-end">
          <Typography variant="caption">
            {selectedRows.length} item{selectedRows.length > 1 && 's'} selected
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

/**
 * Custom hook to get authorizations from the logged in user
 * But no need for SUPER ADMIN
 * @param {*} userProfile
 */
export const useGetAuthorizations = userProfile => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If the logged in is a SUPER ADMIN, no need to get its authorizations
    if (userProfile.userType !== 'SUPER_ADMIN') {
      setLoading(true);
      dataProvider
        .getManyReference('userauthorizations', {
          filter: { userId: userProfile.userId }
        })
        .then(({ data }) => {
          setData(data);
          setLoading(false);
        })
        .catch(error => {
          notify(getIcError(error), { type: 'error' });
          setError(error);
          setLoading(false);
        });
    }
  }, [userProfile.userType, dataProvider, userProfile.userId, notify]);

  return {
    data,
    loading,
    error
  };
};

const ContactsStep = ({ operation, investorId, userProfile, ...props }) => {
  const { contactsContext, setContactsContext } = useContext(
    CopyContactsContext
  );
  //Get ALL the funds related to the investors we choose before
  let fundsRelation = useQuery({
    type: 'getManyReference',
    resource: 'fundinvestors',
    payload: {
      target: 'investorId',
      id: `[${contactsContext.investors}]`
    }
  });

  const {
    data: userConnectedAuthorizations,
    loading: userConnectedAuthorizationsLoading
  } = useGetAuthorizations(userProfile);

  const userConnectedAuthorizationIds = useMemo(() => {
    // If the logged in is a SUPER ADMIN, no need to get ids
    if (userProfile.userType === 'SUPER_ADMIN') return null;
    return userConnectedAuthorizations.map(auth => {
      return `${auth.fundId}:${auth.groupId}`;
    });
  }, [userConnectedAuthorizations, userProfile.userType]);

  const selectedUsers = useRef([]);
  const notify = useNotify();
  const refresh = useRefresh();
  const onSubmit = useCallback(
    async values => {
      let userAuthorizationsArray = [];
      //map for each relation between the investors we choose to see how much funds they are related to.
      (fundsRelation.data || []).forEach(({ fundId, investorId }) => {
        //Then we map on all the investors that we choosed before
        contactsContext.investors.forEach(investorChoosen => {
          //Do a check to see which investor id we are currently on, and select the fundId related to him
          if (investorId === investorChoosen) {
            //Only choose the users that we have clicked to copy them
            intersectionBy(
              values.users,
              selectedUsers.current,
              'userId'
            ).forEach(user => {
              // And then create the object with the groupId, investorId, userId and fundId for the userauthorization
              user.selectedGroups.forEach(groupId => {
                // If ids is null, the logged in is a SUPER ADMIN, so no need to check authorizations
                if (userConnectedAuthorizationIds) {
                  const authId = `${fundId}:${groupId}`;
                  // Add authorization only when the logged in user has the permission
                  if (userConnectedAuthorizationIds.includes(authId)) {
                    let finalUser = {
                      userId: user.userId,
                      investorId,
                      groupId,
                      fundId
                    };
                    userAuthorizationsArray.push(finalUser);
                  }
                } else {
                  let finalUser = {
                    userId: user.userId,
                    investorId,
                    groupId,
                    fundId
                  };
                  userAuthorizationsArray.push(finalUser);
                }
              });
            });
          }
        });
      });
      try {
        if (userAuthorizationsArray.length > 0) {
          await EntitlementApi.createAuthorizations(
            orderBy(userAuthorizationsArray, 'investorId')
          );
          notify('ra.notification.copy_contacts_successfull');
          setContactsContext({
            currentForm: undefined
          });
          await props.onSubmitEnd();
          refresh();
        }
      } catch (e) {
        notify(getIcError(e), { type: 'error' });
      }
    },
    [
      contactsContext.investors,
      fundsRelation,
      notify,
      props,
      refresh,
      setContactsContext,
      userConnectedAuthorizationIds
    ]
  );

  const { data: userauthorizations = [] } = useQuery({
    type: 'getManyReference',
    resource: 'userauthorizations',
    payload: {
      filter: {
        investorId: investorId
      }
    }
  });

  // Render after querying the recipients
  const { data, loading } = useQuery({
    type: 'getManyReference',
    resource: 'investorcontacts',
    payload: {
      target: 'investorId',
      id: investorId,
      sort: {
        field: 'firstName',
        order: 'ASC'
      }
    }
  });

  if (fundsRelation.loading || loading || userConnectedAuthorizationsLoading)
    return <Loading className={props.classes.loading} />;
  if (data) {
    let userTree;

    userTree = data.reduce((contacts, user) => {
      let groups = [];
      userauthorizations &&
        userauthorizations.forEach(userauthorization => {
          if (userauthorization.userId === user.userId) {
            groups.push(userauthorization.groupId);
          }
        });
      if (groups.length > 0) {
        user.groups = uniq(groups);
        user.selectedGroups = uniq(groups);
        return contacts.concat(user);
      } else {
        return contacts;
      }
    }, []);

    return (
      <RecordContextProvider value={{ users: userTree }}>
        <CustomSimpleForm
          save={onSubmit}
          toolbar={null}
          // We override the redux-form name to avoid collision with the react-admin main form
          resource="investorcontacts"
          variant="standard"
          margin="normal"
        >
          <FormSpy>
            {formprops => {
              if (contactsContext.currentForm === undefined) {
                setContactsContext({
                  ...contactsContext,
                  currentForm: formprops.form
                });
              }
              return (
                <Grid container spacing={1}>
                  <Grid item sm={12}>
                    <ContactsList
                      investorId={investorId}
                      userTree={userTree}
                      selectedUsers={selectedUsers}
                      {...props}
                    />
                  </Grid>
                </Grid>
              );
            }}
          </FormSpy>
        </CustomSimpleForm>
      </RecordContextProvider>
    );
  }
};

export default withStyles(styles)(ContactsStep);
